import {
  AfterViewInit,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { EsriExpand, EsriLegend } from '../../../config/esri.module';
import { LayerContainerService } from '../../../shared/service/layer-container.service';
import { MainMapService } from '../../../shared/service/main-map.service';
import { DomUtilService } from '../../../shared/service/dom-util.service';
import { WidgetNotifyService } from '../../../shared/service/widget-notify.service';
import { TranslateService } from '@ngx-translate/core';
import { BaseWidget } from '../base-widget/base-widget';
import { Subscription } from 'rxjs';

@Component({
    selector: 'legend',
    templateUrl: './legend.component.html',
    standalone: false
})
export class LegendComponent extends BaseWidget implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('legendComponentRef', { static: true }) legendComponentRef: ElementRef;
  @ViewChild('legendRef', { static: true }) legendRef: ElementRef;

  private mapView: __esri.MapView;
  // The legend widget
  private legend: __esri.Legend;
  private langChangeSubscription: Subscription;

  constructor(
    public mainMapService: MainMapService,
    public widgetNotifyService: WidgetNotifyService,
    private layerContainerService: LayerContainerService,
    private domUtilService: DomUtilService,
    private translateService: TranslateService
  ) {
    super(mainMapService, widgetNotifyService);
  }

  ngOnInit() {
    // get MainMapService
    this.mapView = this.mainMapService.mapView;
    this.initializeLegend();
    this.setupExpandWidget();
    this.initWidgetListeners();
    this.addWidgetToMap();
    this.subscribeToLangChange();
  }

  ngAfterViewInit() {
    this.updateTooltipOnLanguageChange();

    const containerElement = this.legend.container as HTMLDivElement;

    const observeChildNodes = (element: HTMLElement) => {
      const observer = new MutationObserver(mutationsList => {
        mutationsList.forEach(mutation => {
          if (mutation.type === 'childList') {
            mutation.addedNodes.forEach(addedNode => {
              if (addedNode instanceof HTMLElement) {
                observeChildNodes(addedNode);
                addedNode.childNodes.forEach(node => {
                  if (node instanceof HTMLElement) {
                    const divs = this.domUtilService.findLegendElements(node);
                    for (const item of divs) {
                      // console.log('new node added ', item);
                      this.translateInnerHtml(item);
                    }
                  }
                });
              }
            });
          }
        });
      });
      const config = { childList: true, subtree: true };
      observer.observe(element, config);
    };
    observeChildNodes(containerElement);
  }

  ngOnDestroy() {
    if (this.langChangeSubscription) {
      this.langChangeSubscription.unsubscribe();
    }
  }

  private initializeLegend(): void {
    this.createLegend();
  }

  private setupExpandWidget(): void {
    const expandProperties: __esri.ExpandProperties = {
      view: this.mapView,
      id: 'LegendComponent',
      content: this.legendComponentRef.nativeElement,
      expandIcon: 'list',
    };
    this.expandWidget = new EsriExpand(expandProperties);
    this.translateToolTipWidgetIcon(
      this.expandWidget,
      this.translateService,
      'WIDGET.LEGEND.TOOLTIP'
    );
  }

  private addWidgetToMap(): void {
    this.mapView.ui.add(this.expandWidget, 'top-right');
  }

  private subscribeToLangChange(): void {
    this.langChangeSubscription = this.translateService.onLangChange.subscribe(() => {
      location.reload();
    });
  }

  private updateTooltipOnLanguageChange(): void {
    this.langChangeSubscription = this.translateService.onLangChange.subscribe(() => {
      this.translateToolTipWidgetIcon(
        this.expandWidget,
        this.translateService,
        'WIDGET.LEGEND.TOOLTIP'
      );
    });
  }
  /**
   * @name createLegend
   * @description Creates a new ESRI legend widget
   */
  private createLegend() {
    if (this.legend) {
      this.legend.destroy();
    }
    const legendLayerInfosList = this.buidLegendLayerInfos();
    // create legend div. this div is used then if the legend is destroyed the legendRef should not be destroyed.
    const legendDiv: HTMLDivElement = this.domUtilService.createLegendDiv();
    this.legendRef.nativeElement.appendChild(legendDiv);

    // create legend widget
    const legendProperties: __esri.LegendProperties = {
      view: this.mapView,
      layerInfos: legendLayerInfosList,
      container: legendDiv,
    };
    this.legend = new EsriLegend(legendProperties);
  }

  private buidLegendLayerInfos(): __esri.LegendLayerInfos[] {
    return this.layerContainerService.layerList.map(item => {
      const legendLayerInfos: __esri.LegendLayerInfos = {
        layer: item.esriLayer,
        title: this.translateService.instant(item.esriLayer.title),
      };
      this.translateSublayers(legendLayerInfos);
      return legendLayerInfos;
    });
  }

  private translateSublayers(legendInfos: __esri.LegendLayerInfos): void {
    const sublayers = legendInfos.layer['sublayers']?.items;
    if (sublayers) {
      sublayers.forEach(this.translateListItem.bind(this));
    }
  }

  private translateListItem(item: any) {
    if (item.title) {
      item.title = this.translateService.instant(item.title);
      if (item.sublayers?.items) {
        this.translateSublayersRecursive(item.sublayers.items);
      }
    }
  }

  private translateSublayersRecursive(sublayers: __esri.Collection): void {
    sublayers.forEach(this.translateListItem.bind(this));
  }

  private translateInnerHtml(item: any) {
    if (item.innerHTML && item.innerHTML !== '') {
      const key = `JSON_CONFIG.LAYERLIST.${item.innerHTML}` ;
      this.translateService.get(key).subscribe(translate => {
        if (key !== translate) {
          item.innerHTML = translate;
        }
      });
    }
  }
}
